import clsx from "clsx";

import { typedObjectKeys } from "common/helpers/utils";
import type { WorkspaceAccount } from "common/types";

import { SSI_ITEMS } from "../../constants";
import useAccountSSI from "../../datahooks/useAccountSSI";
import SSIChart from "./SSIChart";

const MAX_SSI_VALUE = 25;

function SSIItem({
  itemKey,
  value,
}: {
  itemKey: keyof typeof SSI_ITEMS;
  value: number;
}) {
  const { label, bgClassName, textClassName } = SSI_ITEMS[itemKey];

  const barWidthPercentage = (value / MAX_SSI_VALUE) * 100;

  return (
    <div className="flex flex-col border-black-200 border rounded-3xl py-2 px-3">
      <div className="flex mb-3 gap-x-3 justify-between">
        <span className="text-black-500 text-button-xs">{label}</span>
        <span className={clsx("text-xs-bold", textClassName)}>
          {value.toFixed(2)}
        </span>
      </div>
      <div className="mb-1.5 w-full h-1 bg-black-200 rounded-full relative">
        <div
          className={clsx(
            "absolute h-full left-0 rounded-full transition-[width]",
            bgClassName,
          )}
          style={{ width: `${barWidthPercentage}%` }}
        />
      </div>
      <div className="flex justify-between text-button-xs text-black-500">
        <span>0</span>
        <span>{MAX_SSI_VALUE}</span>
      </div>
    </div>
  );
}

export default function AccountSSI({ account }: { account: WorkspaceAccount }) {
  const { accountSSI } = useAccountSSI(account);

  return (
    <div className="flex flex-col gap-y-2">
      <SSIChart accountSSI={accountSSI} />
      {typedObjectKeys(SSI_ITEMS).map((key) => {
        const value = accountSSI
          ? accountSSI.subScores.find(({ pillar }) => pillar === key).score
          : 0;
        return <SSIItem key={key} itemKey={key} value={value} />;
      })}
    </div>
  );
}
